import { i18n } from '@/shared/i18n';
import type { ConnectorPlatform } from '@/entities/media-connector';
import type { DeliveryJob, JobStatus } from '@/entities/media-campaign';

/**
 * Job statuses collapsed into the states someone actually filters by.
 *
 * `open` is the useful one: the whole point of the Jobs list is finding the work
 * that still needs a person, which spans "needs review", "ready" and a publish
 * that only got halfway.
 */
export type JobStatusFilter = 'open' | 'published' | 'failed';

export const JOB_STATUS_FILTER_LABELS: Record<JobStatusFilter, string> = {
  get open() {
    return i18n.t('projects:deliveryJobGrid.filters.needsWork');
  },
  get published() {
    return i18n.t('projects:deliveryJobGrid.filters.published');
  },
  get failed() {
    return i18n.t('projects:deliveryJobGrid.filters.failed');
  },
};

const OPEN_STATUSES: ReadonlySet<JobStatus> = new Set([
  'parsing',
  'needs_review',
  'ready',
  'publishing',
  'partially_published',
]);

function matchesStatus(job: DeliveryJob, filter: JobStatusFilter): boolean {
  switch (filter) {
    case 'open':
      return OPEN_STATUSES.has(job.status);
    case 'published':
      return job.status === 'published';
    case 'failed':
      return job.status === 'failed';
  }
}

export interface JobFilterState {
  status: JobStatusFilter | null;
  platform: ConnectorPlatform | null;
  search: string;
}

export const EMPTY_JOB_FILTERS: JobFilterState = {
  status: null,
  platform: null,
  search: '',
};

export function hasActiveJobFilters(filters: JobFilterState): boolean {
  return filters.status !== null || filters.platform !== null || filters.search.trim() !== '';
}

/**
 * Search covers the file name as well as the title: people refer to a job by
 * whichever they happen to have in front of them, and the file name is what the
 * client sent.
 */
export function filterJobs(jobs: DeliveryJob[], filters: JobFilterState): DeliveryJob[] {
  const term = filters.search.trim().toLowerCase();

  return jobs.filter((job) => {
    if (filters.status !== null && !matchesStatus(job, filters.status)) return false;
    if (filters.platform !== null && !job.platforms.includes(filters.platform)) return false;
    if (term === '') return true;

    return (
      job.title.toLowerCase().includes(term) ||
      job.source.fileName.toLowerCase().includes(term) ||
      job.createdBy.toLowerCase().includes(term)
    );
  });
}
